import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AdjustmentsHorizontalIcon, ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const initialForm = { DelaiMois: '', IntervalleKilometres: '', EstActive: true };

export default function MaintenanceRuleCustomizer({ user, overview, isPrivacyMode, onChanged }) {
  const [rules, setRules] = useState({ generalRules: [], vehicleRules: [] });
  const [vehicleId, setVehicleId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const vehicles = useMemo(() => overview?.vehicles || [], [overview]);
  const maintenanceTypes = useMemo(() => (
    (overview?.categories || []).flatMap((category) => category.EntretienTypes || [])
  ), [overview]);
  const canCustomize = Boolean(
    user?.EcritureAutorisee
    && Number(user?.Droits?.MaintenanceRuleCustomize || 0) > 0
    && !isPrivacyMode
  );

  const loadRules = useCallback(async () => {
    if (!user?.UtilisateurID || !canCustomize) return;
    const response = await api.get(`/entretien/${user.UtilisateurID}/rules`);
    setRules(response.data || { generalRules: [], vehicleRules: [] });
  }, [canCustomize, user?.UtilisateurID]);

  useEffect(() => {
    if (!canCustomize) return;
    loadRules().catch((error) => {
      console.error('Impossible de charger les règles d’entretien :', error);
      setMessage({ type: 'error', text: 'Impossible de charger les règles personnalisées.' });
    });
  }, [canCustomize, loadRules]);

  useEffect(() => {
    if (!vehicleId && vehicles.length) setVehicleId(String(vehicles[0].VehiculeID));
  }, [vehicleId, vehicles]);

  useEffect(() => {
    if (!typeId && maintenanceTypes.length) setTypeId(String(maintenanceTypes[0].EntretienTypeID));
  }, [maintenanceTypes, typeId]);

  const customRule = useMemo(() => rules.vehicleRules.find((item) => (
    String(item.VehiculeID) === String(vehicleId) && String(item.EntretienTypeID) === String(typeId)
  )), [rules.vehicleRules, typeId, vehicleId]);
  const generalRule = useMemo(() => (
    rules.generalRules.find((item) => String(item.EntretienTypeID) === String(typeId))
  ), [rules.generalRules, typeId]);

  useEffect(() => {
    const source = customRule || generalRule;
    setForm(source ? {
      DelaiMois: source.DelaiMois ?? '',
      IntervalleKilometres: source.IntervalleKilometres ?? '',
      EstActive: Boolean(source.EstActive),
    } : initialForm);
    setMessage(null);
  }, [customRule, generalRule]);

  if (!canCustomize) return null;

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await api.put(`/entretien/${user.UtilisateurID}/rules/${vehicleId}/${typeId}`, {
        DelaiMois: form.DelaiMois === '' ? null : Number(form.DelaiMois),
        IntervalleKilometres: form.IntervalleKilometres === '' ? null : Number(form.IntervalleKilometres),
        EstActive: form.EstActive,
      });
      await loadRules();
      setMessage({ type: 'success', text: 'Règle personnalisée enregistrée.' });
      window.dispatchEvent(new Event('vehicle:notifications-changed'));
      onChanged?.();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Impossible d’enregistrer cette règle.' });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!customRule) return;
    setSaving(true);
    setMessage(null);
    try {
      await api.delete(`/entretien/${user.UtilisateurID}/rules/${vehicleId}/${typeId}`);
      await loadRules();
      setMessage({ type: 'success', text: 'La règle générale est de nouveau appliquée.' });
      window.dispatchEvent(new Event('vehicle:notifications-changed'));
      onChanged?.();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Impossible de réinitialiser cette règle.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-violet-500/25 bg-slate-950/70 p-5 shadow-[0_24px_70px_rgba(2,6,23,0.32)]">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="grid size-10 place-items-center rounded-lg border border-violet-400/25 bg-violet-500/10 text-violet-200">
            <AdjustmentsHorizontalIcon className="size-5" />
          </span>
          <div>
            <h2 className="font-bold text-white">Règles automatiques par véhicule</h2>
            <p className="mt-1 text-xs font-semibold text-slate-500">Affinez les échéances générales pour chaque véhicule.</p>
          </div>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${customRule ? 'border-violet-400/30 bg-violet-500/10 text-violet-200' : 'border-slate-600 bg-slate-900 text-slate-400'}`}>
          {customRule ? 'Personnalisée' : generalRule ? 'Règle générale' : 'Aucune règle générale'}
        </span>
      </div>

      <form onSubmit={save} className="grid gap-4 md:grid-cols-2 xl:grid-cols-[1fr_1fr_160px_190px_auto] xl:items-end">
        <label className="text-sm font-semibold text-slate-300">
          Véhicule
          <select value={vehicleId} onChange={(event) => setVehicleId(event.target.value)} className="mt-2 h-11 w-full rounded-lg border border-white/10 bg-slate-900 px-3 text-slate-100 outline-none focus:border-violet-400" required>
            {vehicles.map((vehicle) => <option key={vehicle.VehiculeID} value={vehicle.VehiculeID}>{vehicle.Nom}</option>)}
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-300">
          Entretien
          <select value={typeId} onChange={(event) => setTypeId(event.target.value)} className="mt-2 h-11 w-full rounded-lg border border-white/10 bg-slate-900 px-3 text-slate-100 outline-none focus:border-violet-400" required>
            {maintenanceTypes.map((type) => <option key={type.EntretienTypeID} value={type.EntretienTypeID}>{type.Nom}</option>)}
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-300">
          Délai (mois)
          <input type="number" min="1" max="1200" value={form.DelaiMois} onChange={(event) => setForm((current) => ({ ...current, DelaiMois: event.target.value }))} className="mt-2 h-11 w-full rounded-lg border border-white/10 bg-slate-900 px-3 text-slate-100 outline-none focus:border-violet-400" placeholder="Optionnel" />
        </label>
        <label className="text-sm font-semibold text-slate-300">
          Intervalle (km)
          <input type="number" min="1" max="10000000" step="0.1" value={form.IntervalleKilometres} onChange={(event) => setForm((current) => ({ ...current, IntervalleKilometres: event.target.value }))} className="mt-2 h-11 w-full rounded-lg border border-white/10 bg-slate-900 px-3 text-slate-100 outline-none focus:border-violet-400" placeholder="Optionnel" />
        </label>
        <div className="flex gap-2">
          <button type="submit" disabled={saving || !vehicleId || !typeId} className="inline-flex h-11 items-center gap-2 rounded-lg bg-violet-600 px-4 text-sm font-bold text-white hover:bg-violet-500 disabled:opacity-50">
            <CheckIcon className="size-4" /> Enregistrer
          </button>
          {customRule && <button type="button" onClick={reset} disabled={saving} className="grid size-11 place-items-center rounded-lg border border-slate-600 text-slate-300 hover:bg-white/5 disabled:opacity-50" title="Reprendre la règle générale"><ArrowPathIcon className="size-4" /></button>}
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-300 md:col-span-2 xl:col-span-5">
          <input type="checkbox" checked={form.EstActive} onChange={(event) => setForm((current) => ({ ...current, EstActive: event.target.checked }))} className="size-4 rounded border-white/20 bg-slate-900 text-violet-500" />
          Activer la planification automatique pour ce véhicule et cet entretien
        </label>
      </form>
      {message && <p className={`mt-4 rounded-lg border px-3 py-2 text-sm font-semibold ${message.type === 'success' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100' : 'border-rose-500/30 bg-rose-500/10 text-rose-100'}`}>{message.text}</p>}
    </section>
  );
}
